import { FC, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { DialogContent, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useEffectOnceWhen } from "rooks";
import { IntegrationsRow } from "./components/rows/IntegrationsRow";
import { ProjectsRow } from "./components/rows/ProjectsRow";
import { SummaryRow } from "./components/rows/SummaryRow";
import { DescriptionRow } from "./components/rows/DescriptionRow";
import { CreateJiraTicketFormActions } from "./components/CreateJiraTicketFormActions";
import { JiraSnackbarLink } from "./components/JiraSnackbarLink";
import { useJiraValidationHook } from "./utils/hooks/useJiraValidation.hook";
import { htmlToJiraADF } from "./utils/helpers/htmlToJiraADF";
import { IssueTypeRowV2 } from "./components/rows/IssueTypeRowV2";
import { UserDefinedCustomRowsV2 } from "./components/rows/custom/UserDefinedCustomRowsV2";
import {
  PostUsersMeJiraIntegrationsByIntegrationIdIssuesApiArg,
  ResourceType,
  useGetUsersMeByTypeIntegrationsAndIntegrationIdQuery,
  usePostUsersMeJiraIntegrationsByIntegrationIdIssuesMutation,
} from "../../../../../services/cloudchipr.api";
import { DialogTitleClosable } from "../../../dialog-utils/DialogTitleClosable";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { currentAccountProviderTypeSelector } from "../../../../../store/account/selectors/current-account/currentAccountProviderTypeSelector";
import { getProviderName } from "../../../../../utils/helpers/providers/getProviderName";
import { jiraDescriptionSelector } from "../../../../../store/integrations/selectors/jira/jiraDescriptionSelector";
import { currentAccountNameIdSelector } from "../../../../../store/account/selectors/current-account/currentAccountNameIdSelector";
import { currentAccountIdSelector } from "../../../../../store/account/selectors/current-account/currentAccountIdSelector";
import { currentAccountSelectedRowsIdsByResourceTypeSelector } from "../../../../../store/account/selectors/current-account/resources/selection/currentAccountSelectedRowsIdsByResourceTypeSelector";
import { getCurrentAccountResourceTypeDataThunk } from "../../../../../store/account/thunks/filters-get/getCurrentAccountResourceTypeDataThunk";

interface CreateJiraTicketDialogProps {
  resourceType: ResourceType;
  onClose(): void;
}

export const validationSchema = Yup.object({
  integrationId: Yup.string().required("This field is required"),
  body: Yup.object({
    project_key: Yup.string().required("This field is required"),
    issue_type_id: Yup.string().required("This field is required"),
    summary: Yup.string().required("This field is required"),
    description: Yup.string().required("This field is required"),
  }),
});

const initialValues: PostUsersMeJiraIntegrationsByIntegrationIdIssuesApiArg = {
  integrationId: "",
  body: {
    account_id: "",
    project_key: "",
    issue_type_id: "",
    summary: "",
    description: "",
    resource_ids: [],
    fields: {},
  },
};

export const CreateJiraTicketDialog: FC<CreateJiraTicketDialogProps> = ({
  resourceType,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const accountType = useAppSelector(currentAccountProviderTypeSelector);
  const accountId = useAppSelector(currentAccountIdSelector);
  const accountName = useAppSelector(currentAccountNameIdSelector);
  const selectedResources = useAppSelector((state) =>
    currentAccountSelectedRowsIdsByResourceTypeSelector(state, resourceType),
  );
  const selectedResourcesIds = Object.keys(selectedResources || {});

  const initialDescription = useAppSelector((state) =>
    jiraDescriptionSelector(state, resourceType),
  );

  const [creteTicket, { isLoading }] =
    usePostUsersMeJiraIntegrationsByIntegrationIdIssuesMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setFieldError }) => {
      const errors = await validateCustomFields(values.body.fields);

      if (errors) {
        errors.forEach((err: Record<string, string>) => {
          const [key, value] = Object.entries(err)[0];
          setFieldError(`body.fields.${key}`, value);
        });

        return;
      }

      try {
        const description = htmlToJiraADF(
          values.body.description || "",
          true,
        ) as string;

        const payload = {
          ...values,
          body: { ...values.body, description },
        };

        const response = await creteTicket(payload).unwrap();

        enqueueSnackbar("Jira ticket successfully created.", {
          variant: "snackbarAlert",
          AlertSnackBarProps: {
            severity: "success",
          },
          action: <JiraSnackbarLink link={response.link} />,
        });

        dispatch(getCurrentAccountResourceTypeDataThunk(resourceType));

        onClose();
      } catch (e: any) {
        enqueueSnackbar(e?.data?.message || "Something went wrong", {
          variant: "snackbarAlert",
          AlertSnackBarProps: {
            severity: "error",
          },
        });
      }
    },
  });

  const {
    isValid,
    values,
    submitForm,
    handleChange,
    setFieldValue,
    errors,
    touched,
  } = formik;

  const validateCustomFields = useJiraValidationHook();

  const { data: selectedIntegration } =
    useGetUsersMeByTypeIntegrationsAndIntegrationIdQuery(
      { type: "jira", integrationId: values.integrationId },
      { skip: !values.integrationId, refetchOnMountOrArgChange: true },
    );

  const projectChangeHandler = useCallback(
    (projectKey: string) => {
      setFieldValue("body.project_key", projectKey);

      let issueTypeId = selectedIntegration?.default_issue_type_id || "";
      if (projectKey !== selectedIntegration?.default_project_key) {
        issueTypeId = "";
      }

      setFieldValue("body.issue_type_id", issueTypeId);
      setFieldValue("body.fields", {});
    },
    [setFieldValue, selectedIntegration],
  );

  const integrationIdSelectHandler = useCallback(
    (integrationId: string, projectKey?: string, issueTypeId?: string) => {
      setFieldValue("integrationId", integrationId);

      projectKey && setFieldValue("body.project_key", projectKey);
      issueTypeId && setFieldValue("body.issue_type_id", issueTypeId);
      setFieldValue("body.fields", {});
    },
    [setFieldValue],
  );

  useEffectOnceWhen(
    () => {
      const provider = accountType
        ? getProviderName(accountType, { title: true })
        : "";

      setFieldValue("body.summary", `Cloudchipr - ${provider} ${accountName}`);
      setFieldValue("body.description", initialDescription);
      setFieldValue("body.resource_ids", selectedResourcesIds);
      setFieldValue("body.account_id", accountId);
    },
    !!(
      accountName &&
      accountType &&
      selectedResourcesIds?.length &&
      initialDescription &&
      accountId
    ),
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleClosable title="Create a Jira ticket" onClose={onClose} />

      <DialogContent dividers>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <IntegrationsRow
            integrationId={values.integrationId}
            onChange={integrationIdSelectHandler}
          />

          <ProjectsRow
            onChange={projectChangeHandler}
            integrationId={values.integrationId}
            projectKey={values.body.project_key}
            error={touched.body?.project_key ? errors.body?.project_key : ""}
          />

          <IssueTypeRowV2
            projectKey={values.body.project_key}
            error={
              touched.body?.issue_type_id ? errors.body?.issue_type_id : ""
            }
            integrationId={values.integrationId}
            onChange={handleChange}
            setFieldValue={setFieldValue}
            selectedIssueId={values.body.issue_type_id}
          />

          <UserDefinedCustomRowsV2
            errors={errors}
            projectKey={values.body.project_key}
            issueType={values.body.issue_type_id}
            onChange={handleChange}
            key={values.integrationId}
            setFieldValue={setFieldValue}
            integrationId={values.integrationId}
            values={
              values.body as PostUsersMeJiraIntegrationsByIntegrationIdIssuesApiArg["body"]
            }
          />

          <SummaryRow summary={values.body.summary} onChange={handleChange} />

          <DescriptionRow
            setFieldValue={setFieldValue}
            description={values.body.description || ""}
          />
        </Grid>
      </DialogContent>

      <CreateJiraTicketFormActions
        disabled={!isValid}
        onCancel={onClose}
        onSubmit={submitForm}
        isLoading={isLoading}
      />
    </Dialog>
  );
};
